import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { operationalQueryPolicy, referenceQueryPolicy } from '../../api/queryPolicies';
import { Link } from 'react-router-dom';
import {
  searchServiceLanesPage,
  getFleetSuggestionsPage,
  getAdminInterests,
} from '../../api/endpoints/admin';
import type {
  KycStatus,
  ServiceLaneSearchResponse,
  ShipmentResponse,
} from '../../api/types';
import { SectionCard } from '../../components/ui/SectionCard';
import { DataTable } from '../../components/ui/DataTable';
import { StatusPill } from '../../components/ui/StatusPill';
import { Pagination } from '../../components/ui/Pagination';
import { SearchableSelect, type SearchableOption } from '../../components/ui/SearchableSelect';
import { kycStatusColor } from '../../components/domain/statusColor';
import {
  INDIAN_STATES,
  normalizeStateId,
  inferStateId,
  formatStateName,
  formatStateWithCapital,
} from '../../config/indianStates';
import { findTruckType } from '../../config/truckCatalog';
import { useTruckCatalog } from '../../hooks/useTruckCatalog';
import { truckTypeName } from './truckTypeLabel';
import { StatusIcon } from '../../components/ui/StatusIcon';
import { PartnerAlertCell } from './PartnerAlertCell';
import {
  parseScoreBreakdown,
  getScoreTheme,
  humanizeFactorName,
  isNotificationRunActive,
} from './suggestionUtils';
import styles from './FleetManagerSuggestions.module.css';

const STATE_OPTIONS: SearchableOption[] = INDIAN_STATES.map((s) => ({
  id: s.id,
  label: s.name,
  subtitle: s.capital,
}));

const SUGGESTION_PAGE_SIZE = 10;

function humanizeTruckType(t: string) {
  return t.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

interface ScoreBadgeProps {
  score: number;
  rankOrder?: number;
  breakdown?: Record<string, number>;
}

function ScoreBadge({ score, rankOrder, breakdown }: ScoreBadgeProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const theme = getScoreTheme(score);
  const factors = breakdown ? Object.entries(breakdown) : [];

  return (
    <div className={styles.scoreContainer}>
      <button
        type="button"
        className={styles.scorePill}
        style={{
          backgroundColor: theme.bg,
          color: theme.text,
          borderColor: theme.border,
        }}
        onClick={() => setOpen((prev) => !prev)}
        title={t('shipments.fleetManagerSuggestions.scoreTitle')}
      >
        <span>{Math.round(score)}%</span>
        {rankOrder && <span className={styles.scorePillRank}>#{rankOrder}</span>}
      </button>

      {open && (
        <div className={styles.scorePopover}>
          <div className={styles.popoverTitle}>
            <span>{t('shipments.fleetManagerSuggestions.factorBreakdown')}</span>
            <span className={styles.popoverScore}>{Math.round(score)} / 100</span>
          </div>
          {factors.length === 0 ? (
            <div className={styles.subduedNote} style={{ margin: 0 }}>
              {t('shipments.fleetManagerSuggestions.noBreakdown')}
            </div>
          ) : (
            <div className={styles.factorList}>
              {factors.map(([name, val]) => (
                <div key={name} className={styles.factorRow}>
                  <div className={styles.factorHeader}>
                    <span className={styles.factorName}>{humanizeFactorName(name)}</span>
                    <span className={styles.factorScore}>{Math.round(val)}%</span>
                  </div>
                  <div className={styles.barTrack}>
                    <div
                      className={styles.barFill}
                      style={{
                        width: `${Math.min(100, Math.max(0, val))}%`,
                        backgroundColor:
                          val >= 80 ? 'var(--status-green-text)' : val >= 50 ? 'var(--status-amber-text)' : 'var(--muted)',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface ContactSelectorProps {
  lane: ServiceLaneSearchResponse;
  selectedPhone?: string;
  onSelectPhone: (number: string) => void;
}

/** Phone pills only: the load alert goes through the recorded "Send WhatsApp" button (R44). */
function ContactSelector({ lane, selectedPhone, onSelectPhone }: ContactSelectorProps) {
  const options: { key: string; label: string; number: string }[] = [];

  if (lane.whatsappNumber) {
    options.push({ key: 'wa', label: 'WA', number: lane.whatsappNumber });
  }
  if (lane.phoneNumber) {
    options.push({ key: 'mob', label: 'Mob', number: lane.phoneNumber });
  }
  if (lane.secondaryPhoneNumber) {
    options.push({ key: 'sec', label: 'Sec', number: lane.secondaryPhoneNumber });
  }

  const activePhone = selectedPhone || lane.whatsappNumber || lane.phoneNumber || '';

  if (options.length === 0) {
    return <span className={styles.subduedNote} style={{ margin: 0 }}>—</span>;
  }

  return (
    <div className={styles.contactGroup}>
      {options.map((opt) => {
        const isSelected = activePhone === opt.number;
        return (
          <button
            type="button"
            key={opt.key}
            className={`${styles.phonePill} ${isSelected ? styles.phonePillActive : ''}`}
            onClick={() => onSelectPhone(opt.number)}
            aria-pressed={isSelected}
            title={`Select ${opt.label} (${opt.number})`}
          >
            <span className={styles.phoneLabel}>{opt.label}:</span>
            <span>{opt.number}</span>
            {isSelected && (
              <span className={styles.activeCheck}>
                <StatusIcon name="check" size={11} />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

interface Props {
  shipment: ShipmentResponse;
  onPlaceBid?: (fleetManagerId: number) => void;
}

export function FleetManagerSuggestions({ shipment, onPlaceBid }: Props) {
  const { t } = useTranslation();
  const fromState =
    normalizeStateId(shipment.pickupAddress?.state) ??
    inferStateId(
      shipment.pickupAddress?.city,
      shipment.originCity,
      shipment.pickupAddress?.line,
      shipment.originAddress,
    );
  const toState =
    normalizeStateId(shipment.dropAddress?.state) ??
    inferStateId(
      shipment.dropAddress?.city,
      shipment.destinationCity,
      shipment.dropAddress?.line,
      shipment.destinationAddress,
    );
  const catalog = useTruckCatalog();
  // A catalog type (the server resolved its name, or a known id) filters lanes; an "Other"
  // shipment has no catalog type and matches any truck (#249 Contract section 1).
  const truckType =
    shipment.vehicleTypeRequired &&
    (shipment.vehicleTypeName?.trim() ||
      catalog.find(shipment.vehicleTypeRequired) ||
      findTruckType(shipment.vehicleTypeRequired))
      ? shipment.vehicleTypeRequired
      : null;
  const hasOrigin = !!fromState;

  const suggestionContext = JSON.stringify([shipment.id, fromState, toState, truckType, shipment.status]);
  const [suggestionPaging, setSuggestionPaging] = useState({ context: suggestionContext, page: 0 });
  const suggestionPage = suggestionPaging.context === suggestionContext ? suggestionPaging.page : 0;
  if (suggestionPaging.context !== suggestionContext) {
    setSuggestionPaging({ context: suggestionContext, page: 0 });
  }
  const suggestionsQuery = useQuery({
    // Share lane invalidation with lane editing; page/filter keys prevent cross-scope stale rows.
    queryKey: ['admin', 'service-lanes', 'suggestions', shipment.id, fromState, toState, truckType, shipment.status, suggestionPage, SUGGESTION_PAGE_SIZE],
    queryFn: ({ signal }) => getFleetSuggestionsPage(shipment.id, {
      fromState: fromState!, toState: toState ?? undefined, truckType: truckType ?? undefined,
      page: suggestionPage, size: SUGGESTION_PAGE_SIZE,
    }, signal),
    enabled: hasOrigin,
    ...operationalQueryPolicy,
  });
  const suggestionPages = suggestionsQuery.data?.totalPages ?? 0;
  useEffect(() => {
    if (suggestionsQuery.data && suggestionPage >= Math.max(1, suggestionPages)) {
      setSuggestionPaging({ context: suggestionContext, page: Math.max(0, suggestionPages - 1) });
    }
  }, [suggestionContext, suggestionPage, suggestionPages, suggestionsQuery.data]);

  const suggestions = useMemo(() => (suggestionsQuery.data?.items ?? []).map((row) => {
    const breakdown = parseScoreBreakdown(row.match?.scoreBreakdown);
    const laneScore = breakdown['LANE_MATCH'] ?? breakdown['lane_match'];
    return {
      ...row,
      matchType: row.match ? (laneScore !== undefined && laneScore < 80 ? 'ORIGIN_ONLY' : 'EXACT_LANE') : row.matchType,
      matchScore: row.match ? Number(row.match.matchScore) : undefined,
      rankOrder: row.match?.rankOrder,
      scoreBreakdown: row.match ? breakdown : undefined,
    };
  }), [suggestionsQuery.data]);
  const isLoading = hasOrigin && suggestionsQuery.isLoading;

  // Contact selection is kept by lane ID across pages.
  const [selectedPhones, setSelectedPhones] = useState<Record<number, string>>({});
  const handleSelectPhone = (laneId: number, number: string) => {
    setSelectedPhones((prev) => ({ ...prev, [laneId]: number }));
  };

  const interestsQuery = useQuery({
    queryKey: ['admin', 'interests', 'shipment', shipment.shipmentUuid],
    queryFn: () => getAdminInterests({ shipmentUuid: shipment.shipmentUuid || undefined, size: 50 }),
    enabled: !!shipment.shipmentUuid,
    ...operationalQueryPolicy,
  });

  const vendorsWithInterest = useMemo(() => {
    const set = new Set<number>();
    if (interestsQuery.data?.items) {
      for (const item of interestsQuery.data.items) {
        if (item.status !== 'CLOSED' && item.status !== 'WITHDRAWN') {
          set.add(item.vendorId);
        }
      }
    }
    return set;
  }, [interestsQuery.data]);

  // Manual search state
  const [mFrom, setMFrom] = useState('');
  const [mTo, setMTo] = useState('');
  const [mTruck, setMTruck] = useState('');
  const manualEnabled = !!(mFrom || mTo || mTruck);
  const manualContext = JSON.stringify([shipment.id, mFrom, mTo, mTruck]);
  const [manualPaging, setManualPaging] = useState({ context: manualContext, page: 0 });
  const manualPage = manualPaging.context === manualContext ? manualPaging.page : 0;
  if (manualPaging.context !== manualContext) {
    setManualPaging({ context: manualContext, page: 0 });
  }
  const manual = useQuery({
    queryKey: ['admin', 'service-lanes', 'search-page', mFrom, mTo, mTruck, manualPage, SUGGESTION_PAGE_SIZE],
    queryFn: ({ signal }) => searchServiceLanesPage({
      fromState: mFrom || undefined, toState: mTo || undefined, truckType: mTruck || undefined,
      page: manualPage, size: SUGGESTION_PAGE_SIZE,
    }, signal),
    enabled: manualEnabled,
    ...referenceQueryPolicy,
  });
  const manualRows = manual.data?.items ?? [];
  const manualPages = manual.data?.totalPages ?? 0;
  useEffect(() => {
    if (manual.data && manualPage >= Math.max(1, manualPages)) {
      setManualPaging({ context: manualContext, page: Math.max(0, manualPages - 1) });
    }
  }, [manualContext, manualPage, manualPages, manual.data]);

  const truckName = truckType ? truckTypeName(shipment, (id) => catalog.find(id)?.truck ?? findTruckType(id)?.truck) : null;
  // NOTE: Wave telemetry banner removed — notification history is now available
  // on-demand via the "View notification history" button in the shipment header.
  const activeRun = undefined as import('../../api/types').NotificationRunResponse | undefined;
  const haltedEarly =
    activeRun?.status === 'COMPLETED' &&
    activeRun?.errorMessage?.toLowerCase().includes('minimum bids');


  return (
    <SectionCard title={t('shipments.fleetManagerSuggestions.sectionTitle')}>
      <div className={styles.body}>
        {/* Automated Wave Telemetry Banner */}
        {activeRun && (
          <div
            className={`${styles.waveBanner} ${
              activeRun.status === 'COMPLETED'
                ? styles.waveBannerHalted
                : ''
            }`}
          >
            <div className={styles.waveLeft}>
              <div
                className={`${styles.waveIconWrap} ${
                  activeRun.status === 'COMPLETED'
                    ? styles.waveIconWrapHalted
                    : ''
                }`}
              >
                {activeRun.status === 'IN_PROGRESS'
                  ? '🌊'
                  : isNotificationRunActive(activeRun.status)
                    ? '⏳'
                    : '✓'}
              </div>
              <div>
                <div className={styles.waveHeading}>
                  {activeRun.status === 'IN_PROGRESS' && (
                    <>
                      <span className={styles.pulseDot} />
                      <span>
                        {t('shipments.fleetManagerSuggestions.waveActive', { current: activeRun.currentWave, total: activeRun.totalWaves })}
                      </span>
                    </>
                  )}
                  {haltedEarly ? (
                    <span>{t('shipments.fleetManagerSuggestions.earlyHalt')}</span>
                  ) : (
                    activeRun.status === 'COMPLETED' && (
                      <span>{t('shipments.fleetManagerSuggestions.runCompleted', { total: activeRun.totalWaves })}</span>
                    )
                  )}
                  {activeRun.status === 'SCHEDULED' && <span>{t('shipments.fleetManagerSuggestions.waveScheduled')}</span>}
                  {activeRun.status === 'CANCELLED' && <span>{t('shipments.fleetManagerSuggestions.runCancelled')}</span>}
                  {activeRun.status === 'FAILED' && <span>{t('shipments.fleetManagerSuggestions.runFailed')}</span>}
                </div>
                <div className={styles.waveSub}>
                  {activeRun.vendorsContacted}
                  {t('shipments.fleetManagerSuggestions.waveVendor')}
                  {activeRun.vendorsContacted === 1 ? '' : 's'}
                  {t('shipments.fleetManagerSuggestions.waveAlerted')}
                  {activeRun.policyVersion}
                </div>
              </div>
            </div>
            <div className={styles.waveMeta}>
              <span
                className={styles.waveBadge}
                style={{
                  backgroundColor:
                    isNotificationRunActive(activeRun.status)
                      ? 'var(--action-primary-soft)'
                      : activeRun.status === 'COMPLETED'
                      ? 'var(--status-green-bg)'
                      : 'var(--status-gray-bg)',
                  color:
                    isNotificationRunActive(activeRun.status)
                      ? 'var(--accent)'
                      : activeRun.status === 'COMPLETED'
                      ? 'var(--status-green-text)'
                      : 'var(--muted)',
                }}
              >
                {haltedEarly ? 'HALTED EARLY' : activeRun.status.replace(/_/g, ' ')}
              </span>
            </div>
          </div>
        )}

        {hasOrigin && (
          <div className={styles.laneSummary}>
            {formatStateName(fromState!)} {toState ? `→ ${formatStateName(toState!)}` : '(Origin hub)'}
            {truckName && <span className={styles.laneTruck}> · {truckName}</span>}
          </div>
        )}

        {!hasOrigin && (
          <div className={styles.notice}>
            {t('shipments.fleetManagerSuggestions.noOrigin')}
            {[
              shipment.originCity || shipment.pickupAddress?.state,
              shipment.destinationCity || shipment.dropAddress?.state,
            ]
              .map((s) => `"${s || '—'}"`)
              .join(' → ')}
            {t('shipments.fleetManagerSuggestions.noOriginSuffix')}
          </div>
        )}
        {hasOrigin && !truckType && shipment.vehicleTypeRequired && (
          <div className={styles.subduedNote}>
            {t('shipments.fleetManagerSuggestions.truckTypeUnknown', { truckType: shipment.vehicleTypeRequired })}
          </div>
        )}

        {hasOrigin && (
          <>
            {isLoading && <div className={styles.subduedNote}>{t('shipments.fleetManagerSuggestions.loading')}</div>}
            {suggestionsQuery.isError && (
              <div className={styles.error}>{t('shipments.fleetManagerSuggestions.loadFailed')}</div>
            )}
            {!isLoading && !suggestionsQuery.isError && suggestions.length === 0 && (
              <div className={styles.subduedNote}>{t('shipments.fleetManagerSuggestions.noSuggestions')}</div>
            )}
            {!isLoading && suggestions.length > 0 && (
              <div className={styles.suggestionList}>
                {suggestions.map((suggestion) => {
                  const { lane, truckTypeMatch, matchType, matchScore, rankOrder, scoreBreakdown } =
                    suggestion;

                  return (
                    <div key={lane.fleetManagerId} className={styles.suggestionRow}>
                      <div className={styles.suggestionMain}>
                        <Link
                          to={`/users/${lane.fleetManagerId}`}
                          state={{ from: `/shipments/${shipment.id}` }}
                          className={styles.link}
                        >
                          {lane.companyName || 'Unknown partner'}
                        </Link>
                        {lane.verificationStatus && (
                          <StatusPill
                            bg={kycStatusColor(lane.verificationStatus as KycStatus).bg}
                            text={kycStatusColor(lane.verificationStatus as KycStatus).text}
                            label={lane.verificationStatus}
                          />
                        )}

                        {/* Algorithmic Match Score & Popover */}
                        {matchScore !== undefined ? (
                          <ScoreBadge
                            score={matchScore}
                            rankOrder={rankOrder}
                            breakdown={scoreBreakdown}
                          />
                        ) : (
                          <span
                            className={
                              truckTypeMatch
                                ? `${styles.matchBadge} ${styles.matchBadgeTruck}`
                                : matchType === 'ORIGIN_ONLY'
                                ? `${styles.matchBadge} ${styles.matchBadgeOrigin}`
                                : styles.matchBadge
                            }
                          >
                            {matchType === 'ORIGIN_ONLY'
                              ? (truckTypeMatch ? 'Origin + truck' : 'Origin only')
                              : (truckTypeMatch ? 'Lane + truck' : 'Lane only')}
                          </span>
                        )}

                        {matchType === 'ORIGIN_ONLY' && (
                          <span className={styles.originMatchPill} title={t('shipments.fleetManagerSuggestions.originMatchTitle')}>
                            {t('shipments.fleetManagerSuggestions.originMatch')}
                          </span>
                        )}

                        {/* Interest Expressed Badge */}
                        {vendorsWithInterest.has(lane.fleetManagerId) && (
                          <span
                            style={{
                              padding: '3px 8px',
                              borderRadius: 'var(--radius-pill)',
                              fontSize: 11,
                              fontWeight: 700,
                              background: 'var(--action-primary-soft)',
                              color: 'var(--accent)',
                              border: '1px solid var(--line-strong)',
                            }}
                          >
                            {t('shipments.fleetManagerSuggestions.interestExpressed')}
                          </span>
                        )}

                        {onPlaceBid && shipment.status === 'POSTED' && (
                          <button
                            type="button"
                            className={styles.bidBtn}
                            onClick={() => onPlaceBid(lane.fleetManagerId)}
                          >
                            {t('shipments.fleetManagerSuggestions.placeBid')}
                          </button>
                        )}
                      </div>

                      {/* Contact phones and the partner's load alert state (#289) */}
                      <div className={styles.contactLine}>
                        <ContactSelector
                          lane={lane}
                          selectedPhone={selectedPhones[lane.laneId]}
                          onSelectPhone={(phone) => handleSelectPhone(lane.laneId, phone)}
                        />
                        <PartnerAlertCell
                          shipmentId={shipment.id}
                          vendorId={lane.fleetManagerId}
                          alert={suggestion.whatsappAlert ?? null}
                        />
                      </div>

                      {lane.truckTypes.length > 0 && (
                        <div className={styles.tagList}>
                          {lane.truckTypes.map((truck) => (
                            <span key={truck} className={styles.tag}>
                              {humanizeTruckType(truck)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {!isLoading && (
              <Pagination
                page={suggestionPage}
                totalPages={suggestionPages}
                totalItems={suggestionsQuery.data?.totalItems ?? 0}
                size={SUGGESTION_PAGE_SIZE}
                onPage={(page) => setSuggestionPaging({ context: suggestionContext, page })}
              />
            )}
          </>
        )}

        <div className={styles.divider} />
        <h3 className={styles.searchTitle}>{t('shipments.fleetManagerSuggestions.searchTitle')}</h3>
        <div className={styles.toolbar}>
          <div className={styles.selectWrap}>
            <SearchableSelect
              options={STATE_OPTIONS}
              value={mFrom}
              onChange={setMFrom}
              placeholder={t('shipments.fleetManagerSuggestions.allOrigins')}
              allowEmpty
            />
          </div>
          <div className={styles.selectWrap}>
            <SearchableSelect
              options={STATE_OPTIONS}
              value={mTo}
              onChange={setMTo}
              placeholder={t('shipments.fleetManagerSuggestions.allDestinations')}
              allowEmpty
            />
          </div>
          <select
            className={styles.select}
            value={mTruck}
            onChange={(e) => setMTruck(e.target.value)}
          >
            <option value="">{t('shipments.fleetManagerSuggestions.allTruckTypes')}</option>
            {catalog.trucks.map((truck) => (
              <option key={truck.id} value={truck.id}>
                {truck.name}
              </option>
            ))}
          </select>
        </div>

        {manual.isError && (
          <div className={styles.error}>{t('shipments.fleetManagerSuggestions.searchFailed')}</div>
        )}
        {manualEnabled ? (
          <>
            <DataTable
              loading={manual.isLoading}
              keyFn={(l) => `${l.fleetManagerId}-${l.fromState}-${l.toState}`}
              rows={manualRows}
              empty="No service lanes found matching the filters"
              columns={[
                {
                  key: 'fleetManager',
                  header: 'Partner',
                  render: (l) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <Link
                        to={`/users/${l.fleetManagerId}`}
                        state={{ from: `/shipments/${shipment.id}` }}
                        className={styles.link}
                      >
                        {l.companyName || 'Unknown partner'}
                      </Link>
                      {vendorsWithInterest.has(l.fleetManagerId) && (
                        <span
                          style={{
                            padding: '2px 6px',
                            borderRadius: 'var(--radius-pill)',
                            fontSize: 10.5,
                            fontWeight: 700,
                            background: 'var(--action-primary-soft)',
                            color: 'var(--accent)',
                            border: '1px solid var(--line-strong)',
                          }}
                        >
                          {t('shipments.fleetManagerSuggestions.interest')}
                        </span>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'contact',
                  header: 'Contact',
                  render: (l) => (
                    <ContactSelector
                      lane={l}
                      selectedPhone={selectedPhones[l.laneId]}
                      onSelectPhone={(phone) => handleSelectPhone(l.laneId, phone)}
                    />
                  ),
                },
                {
                  key: 'fleetSize',
                  header: 'Fleet Size',
                  render: (l) => l.fleetSize,
                },
                {
                  key: 'status',
                  header: 'KYC Status',
                  render: (l) => {
                    if (!l.verificationStatus) return '—';
                    const colors = kycStatusColor(l.verificationStatus as KycStatus);
                    return (
                      <StatusPill bg={colors.bg} text={colors.text} label={l.verificationStatus} />
                    );
                  },
                },
                {
                  key: 'route',
                  header: 'Route',
                  render: (l) => (
                    <span>
                      {formatStateWithCapital(l.fromState)} → {formatStateWithCapital(l.toState)}
                    </span>
                  ),
                },
                {
                  key: 'truckTypes',
                  header: 'Truck Types Offered',
                  render: (l) => (
                    <div className={styles.tagList}>
                      {l.truckTypes.map((truck) => (
                        <span key={truck} className={styles.tag}>
                          {humanizeTruckType(truck)}
                        </span>
                      ))}
                    </div>
                  ),
                },
              ]}
            />
            {!manual.isLoading && (
              <Pagination
                page={manualPage}
                totalPages={manualPages}
                totalItems={manual.data?.totalItems ?? 0}
                size={SUGGESTION_PAGE_SIZE}
                onPage={(page) => setManualPaging({ context: manualContext, page })}
              />
            )}
          </>
        ) : (
          <div className={styles.subduedNote}>
            {t('shipments.fleetManagerSuggestions.pickState')}
          </div>
        )}
      </div>
    </SectionCard>
  );
}
